import { format, startOfWeek } from "date-fns";
import { supabase } from "@/lib/supabase";
import { computeMinPlayers, resolveJoinStatus, countGoing, countReserves } from "@/lib/v2/confirmation";
import {
  buildSlotAggregates,
  countDistinctVoters,
  dayVoterCountsForDays,
  type PollDayRow,
  type PollSlotVote,
} from "@/lib/v2/pollAggregates";
import { generateRsvpSalt, hashRsvpPassword } from "@/lib/api/publicRsvp";
import {
  MatchEvent,
  fetchRsvpsForEvent,
  upsertRsvp,
  resolveCurrentUserPlayerId,
} from "./rsvp";
import { dispatchNotification } from "./pushNotifications";

export const MAX_POLL_DAYS = 3;
export const MAX_TIMES_PER_DAY = 3;
/** @deprecated legacy one-option polls */
export const MAX_POLL_OPTIONS = 3;

export type PollMode = "days_only" | "days_with_times";
export type PollStatusV2 = "open" | "confirmed" | "cancelled";

export interface PollSlot {
  id: string;
  poll_id: string;
  poll_day_id: string;
  slot_time: string;
  votes?: PollSlotVoteRow[];
}

export interface PollSlotVoteRow {
  id: string;
  poll_slot_id: string;
  user_id: string | null;
  player_id: number | null;
}

export interface PollDay {
  id: string;
  poll_id: string;
  day: string;
  position: number;
  slots?: PollSlot[];
}

export interface PollV2 {
  id: string;
  group_id: string;
  status: PollStatusV2;
  mode: PollMode;
  question: string | null;
  created_by: string | null;
  closes_at: string | null;
  week_start: string | null;
  poll_public_token?: string | null;
  poll_password_hash?: string | null;
  created_at: string;
  days?: PollDay[];
  /** Current user's selected slot ids */
  my_slot_ids?: string[];
  total_voters?: number;
}

export interface CreatePollV2Input {
  question: string;
  mode: PollMode;
  days: string[];
  dayTimes?: Record<string, string[]>;
  closesAt?: string;
  publicPassword?: string;
  weekStart?: string;
}

/**
 * Thrown when a group already has an open poll for the target week.
 * Business rule §3: max 1 open poll per group per week.
 * The `code` is stable so the UI can map it to a friendly i18n message.
 */
export class OpenPollExistsError extends Error {
  readonly code = "OPEN_POLL_EXISTS_THIS_WEEK";
  constructor(message = "An open poll already exists for this group this week.") {
    super(message);
    this.name = "OpenPollExistsError";
  }
}

/** ISO date (YYYY-MM-DD) of the start of the week (Sunday) containing `date`. */
export function weekStartOf(date: Date = new Date()): string {
  return format(startOfWeek(date, { weekStartsOn: 0 }), "yyyy-MM-dd");
}

export interface Poll {
  id: string;
  group_id: string;
  status: "open" | "confirmed" | "cancelled";
  created_by: string | null;
  closes_at: string | null;
  week_start: string | null;
  poll_public_token?: string | null;
  poll_password_hash?: string | null;
  created_at: string;
  options?: PollOption[];
}

export interface PollOption {
  id: string;
  poll_id: string;
  proposed_date: string;
  label: string | null;
  votes?: PollVote[];
}

export interface PollVote {
  id: string;
  poll_id: string;
  poll_option_id: string;
  user_id: string | null;
  player_id: number | null;
  created_at: string;
}

export interface GroupSettings {
  group_id: string;
  max_players: number;
  min_confirm_pct: number;
  max_reserves: number;
  default_location: string | null;
  default_time: string | null;
  candidate_days: number[];
}

export async function fetchActivePoll(groupId: string): Promise<Poll | null> {
  const { data, error } = await supabase
    .from("polls")
    .select(`
      *,
      options:poll_options(
        *,
        votes:poll_votes(*)
      )
    `)
    .eq("group_id", groupId)
    .eq("status", "open")
    .maybeSingle();

  if (error) throw error;
  return data as unknown as Poll | null;
}

export async function createPoll(
  groupId: string,
  proposedDates: { proposed_date: string; label?: string }[],
  options?: { closesAt?: string; weekStart?: string }
): Promise<Poll> {
  if (proposedDates.length === 0) {
    throw new Error("NO_POLL_OPTIONS");
  }
  if (proposedDates.length > MAX_POLL_OPTIONS) {
    throw new Error("TOO_MANY_POLL_OPTIONS");
  }

  const weekStart = options?.weekStart ?? weekStartOf();

  // Guard (§3): max 1 open poll per group per week. Block if an open poll
  // already exists for the same week before creating a duplicate.
  const { data: openPolls, error: openPollsError } = await supabase
    .from("polls")
    .select("id, week_start, created_at")
    .eq("group_id", groupId)
    .eq("status", "open");

  if (openPollsError) throw openPollsError;

  const hasOpenPollThisWeek = (openPolls ?? []).some((p) => {
    const pollWeek = p.week_start ?? weekStartOf(new Date(p.created_at));
    return pollWeek === weekStart;
  });

  if (hasOpenPollThisWeek) throw new OpenPollExistsError();

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id ?? null;

  // 1. Create the poll
  const { data: poll, error: pollError } = await supabase
    .from("polls")
    .insert({
      group_id: groupId,
      status: "open",
      created_by: userId,
      closes_at: options?.closesAt ?? null,
      week_start: weekStart,
    })
    .select()
    .single();

  if (pollError) throw pollError;

  // 2. Insert poll options
  if (proposedDates.length > 0) {
    const { error: optionsError } = await supabase
      .from("poll_options")
      .insert(
        proposedDates.map((d) => ({
          poll_id: poll.id,
          proposed_date: d.proposed_date,
          label: d.label ?? null,
        }))
      );

    if (optionsError) throw optionsError;
  }

  // Refetch to return full object with options
  return (await fetchActivePoll(groupId)) ?? (poll as unknown as Poll);
}

export async function voteInPoll(
  pollId: string,
  optionId: string,
  voter: { userId?: string; playerId?: number }
): Promise<PollVote> {
  const row = {
    poll_id: pollId,
    poll_option_id: optionId,
    user_id: voter.userId ?? null,
    player_id: voter.playerId ?? null,
  };

  let onConflict = "";
  if (voter.userId) {
    onConflict = "poll_id,user_id";
  } else if (voter.playerId) {
    onConflict = "poll_id,player_id";
  } else {
    throw new Error("Must provide userId or playerId to vote");
  }

  const { data, error } = await supabase
    .from("poll_votes")
    .upsert(row, { onConflict })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as PollVote;
}

export async function fetchGroupSettings(groupId: string): Promise<GroupSettings | null> {
  const { data, error } = await supabase
    .from("group_settings")
    .select("*")
    .eq("group_id", groupId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as GroupSettings | null;
}

export async function updateGroupSettings(
  groupId: string,
  settings: Partial<GroupSettings>
): Promise<GroupSettings> {
  const { data, error } = await supabase
    .from("group_settings")
    .upsert({ group_id: groupId, ...settings })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as GroupSettings;
}

export async function closePollAndCreateEvent(
  pollId: string,
  winningOptionId: string,
  details: { location?: string; maxPlayers?: number }
): Promise<MatchEvent> {
  // 1. Get winning option proposed_date
  const { data: option, error: optionError } = await supabase
    .from("poll_options")
    .select("proposed_date, poll_id, polls(group_id)")
    .eq("id", winningOptionId)
    .single();

  if (optionError) throw optionError;

  const groupId = (option.polls as any).group_id;

  // 2. Fetch group settings
  const settings = await fetchGroupSettings(groupId);
  const maxPlayers = details.maxPlayers ?? settings?.max_players ?? 10;
  const minConfirmPct = settings?.min_confirm_pct ?? 70;
  const minPlayers = computeMinPlayers(maxPlayers, minConfirmPct);
  const location = details.location ?? settings?.default_location ?? null;

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id ?? null;

  // 3. Mark poll confirmed (Path A close flow)
  const { error: pollUpdateError } = await supabase
    .from("polls")
    .update({ status: "confirmed" })
    .eq("id", pollId);

  if (pollUpdateError) throw pollUpdateError;

  const { data: pollCredentials, error: pollCredentialsError } = await supabase
    .from("polls")
    .select("poll_public_token, poll_password_hash")
    .eq("id", pollId)
    .single();

  if (pollCredentialsError) throw pollCredentialsError;

  // 4. Create the match event
  const { data: event, error: eventError } = await supabase
    .from("match_events")
    .insert({
      group_id: groupId,
      event_date: option.proposed_date,
      location,
      max_players: maxPlayers,
      min_players: minPlayers,
      poll_option_id: winningOptionId,
      poll_id: pollId,
      status: "convocatoria",
      created_by: userId,
      rsvp_public_token: pollCredentials.poll_public_token,
      rsvp_password_hash: pollCredentials.poll_password_hash,
    })
    .select()
    .single();

  if (eventError) throw eventError;

  const matchEvent = event as unknown as MatchEvent;
  await migratePollVotersToEvent(pollId, winningOptionId, matchEvent, {
    maxPlayers,
    minConfirmPct,
    maxReserves: settings?.max_reserves ?? 2,
  });

  return matchEvent;
}

/** Auto-RSVP voters who picked the winning poll option as provisional going. */
export async function migratePollVotersToEvent(
  pollId: string,
  winningOptionId: string,
  event: MatchEvent,
  config: { maxPlayers: number; minConfirmPct?: number; maxReserves?: number }
): Promise<void> {
  const { data: votes, error: votesError } = await supabase
    .from("poll_votes")
    .select("user_id, player_id")
    .eq("poll_id", pollId)
    .eq("poll_option_id", winningOptionId);

  if (votesError) throw votesError;
  if (!votes?.length) return;

  let rsvps = await fetchRsvpsForEvent(event.id);
  let goingCount = countGoing(rsvps);
  let reserveCount = countReserves(rsvps);
  const maxReserves = config.maxReserves ?? 2;

  for (const vote of votes) {
    let playerId = vote.player_id as number | null;
    if (!playerId && vote.user_id) {
      playerId = await resolveCurrentUserPlayerId(event.group_id, vote.user_id);
    }
    if (!playerId) continue;

    if (rsvps.some((r) => r.player_id === playerId)) continue;

    const decision = resolveJoinStatus(goingCount, reserveCount, config.maxPlayers, maxReserves);
    if (decision === "full") break;

    const status = decision === "going" ? "going" : "reserve";
    await upsertRsvp(event.id, status, {
      playerId,
      addedByUserId: vote.user_id ?? undefined,
    });

    if (status === "going") goingCount++;
    else reserveCount++;
    rsvps = await fetchRsvpsForEvent(event.id);
  }

  await dispatchNotification({
    groupId: event.group_id,
    type: "poll_to_match",
    eventId: event.id,
    title: "Hay partido asignado",
    body: "Confirmá tu asistencia en la app.",
    url: "/v2/home",
  });
}

// ---------------------------------------------------------------------------
// Path B: two-level poll API (poll_days + poll_slots + poll_slot_votes)
// ---------------------------------------------------------------------------

function mapRpcError(err: { message?: string; code?: string }): Error {
  const msg = err.message ?? "";
  if (msg.includes("OPEN_POLL_EXISTS_THIS_WEEK")) {
    return new OpenPollExistsError();
  }
  if (msg.includes("INVALID_DAY_COUNT")) {
    return new Error("INVALID_DAY_COUNT");
  }
  if (msg.includes("INVALID_TIMES_PER_DAY")) {
    return new Error("INVALID_TIMES_PER_DAY");
  }
  if (msg.includes("ADMIN_REQUIRED")) {
    return new Error("ADMIN_REQUIRED");
  }
  return new Error(msg || "POLL_RPC_FAILED");
}

async function hashPollPassword(password: string): Promise<string> {
  const salt = generateRsvpSalt();
  return `${salt}:${await hashRsvpPassword(password, salt)}`;
}

export async function expireStalePolls(): Promise<number> {
  const { data, error } = await supabase.rpc("expire_stale_polls");
  if (error) throw error;
  return (data as number) ?? 0;
}

export type ActivePollFormat = "v2" | "legacy";

/** Load the open poll for home, falling back to Path A when no poll_days exist. */
export async function fetchActivePollForHome(
  groupId: string
): Promise<{ poll: Poll | PollV2 | null; format: ActivePollFormat }> {
  const v2Poll = await fetchActivePollV2(groupId);
  if (!v2Poll) return { poll: null, format: "v2" };
  if ((v2Poll.days?.length ?? 0) > 0) {
    return { poll: v2Poll, format: "v2" };
  }

  const legacyPoll = await fetchActivePoll(groupId);
  return { poll: legacyPoll, format: "legacy" };
}

export async function fetchActivePollV2(groupId: string): Promise<PollV2 | null> {
  await expireStalePolls().catch(() => 0);

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id ?? null;

  const { data, error } = await supabase
    .from("polls")
    .select(`
      *,
      days:poll_days(
        *,
        slots:poll_slots(
          *,
          votes:poll_slot_votes(id, user_id, player_id, poll_slot_id)
        )
      )
    `)
    .eq("group_id", groupId)
    .eq("status", "open")
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const poll = data as unknown as PollV2;
  poll.days = (poll.days ?? []).sort((a, b) => a.position - b.position);

  const allVotes: PollSlotVote[] = collectPollSlotVotes(poll as PollV2);

  poll.total_voters = countDistinctVoters(allVotes);
  poll.my_slot_ids = userId
    ? allVotes.filter((v) => v.user_id === userId).map((v) => v.poll_slot_id)
    : [];

  return poll;
}

export async function createPollV2(
  groupId: string,
  input: CreatePollV2Input
): Promise<PollV2> {
  if (input.days.length < 1 || input.days.length > MAX_POLL_DAYS) {
    throw new Error("INVALID_DAY_COUNT");
  }

  let passwordHash: string | null = null;
  if (input.publicPassword?.trim()) {
    passwordHash = await hashPollPassword(input.publicPassword.trim());
  }

  const sortedDays = [...input.days].sort();
  const timesPayload: Record<string, string[]> = {};
  if (input.mode === "days_with_times" && input.dayTimes) {
    for (const day of sortedDays) {
      const times = input.dayTimes[day] ?? [];
      if (times.length < 1 || times.length > MAX_TIMES_PER_DAY) {
        throw new Error("INVALID_TIMES_PER_DAY");
      }
      timesPayload[day] = times;
    }
  }

  const { data, error } = await supabase.rpc("open_poll", {
    p_group_id: groupId,
    p_question: input.question,
    p_mode: input.mode,
    p_days: sortedDays,
    p_times: timesPayload,
    p_closes_at: input.closesAt ?? null,
    p_week_start: input.weekStart ?? weekStartOf(),
    p_password_hash: passwordHash,
  });

  if (error) throw mapRpcError(error);

  const poll = await fetchActivePollV2(groupId);
  if (!poll) throw new Error("POLL_CREATE_FAILED");
  return poll;
}

export async function votePollSlots(pollId: string, slotIds: string[]): Promise<void> {
  const { error } = await supabase.rpc("vote_poll_slots", {
    p_poll_id: pollId,
    p_slot_ids: slotIds,
  });
  if (error) throw mapRpcError(error);
}

export async function confirmPollSlot(
  pollId: string,
  slotId: string,
  details?: { location?: string; maxPlayers?: number }
): Promise<MatchEvent> {
  const { data: eventId, error } = await supabase.rpc("confirm_poll_slot", {
    p_poll_id: pollId,
    p_slot_id: slotId,
    p_location: details?.location ?? null,
    p_max_players: details?.maxPlayers ?? null,
  });

  if (error) throw mapRpcError(error);

  const { data: event, error: fetchError } = await supabase
    .from("match_events")
    .select("*")
    .eq("id", eventId as string)
    .single();

  if (fetchError) throw fetchError;

  const matchEvent = event as unknown as MatchEvent;

  await dispatchNotification({
    groupId: matchEvent.group_id,
    type: "poll_to_match",
    eventId: matchEvent.id,
    title: "Hay partido asignado",
    body: "Confirmá tu asistencia en la app.",
    url: "/v2/home",
  });

  return matchEvent;
}

export async function cancelPoll(pollId: string): Promise<void> {
  const { error } = await supabase.rpc("cancel_poll", { p_poll_id: pollId });
  if (error) throw mapRpcError(error);
}

/** Collect raw slot votes from a loaded PollV2 (for distinct-per-day counts). */
export function collectPollSlotVotes(poll: PollV2): PollSlotVote[] {
  const votes: PollSlotVote[] = [];
  for (const day of poll.days ?? []) {
    for (const slot of day.slots ?? []) {
      for (const v of slot.votes ?? []) {
        votes.push({
          poll_slot_id: v.poll_slot_id,
          user_id: v.user_id ?? null,
          player_id: v.player_id ?? null,
        });
      }
    }
  }
  return votes;
}

/** Build view-model aggregates for poll-open UI */
export function buildPollV2Aggregates(poll: PollV2) {
  const days: PollDayRow[] = (poll.days ?? []).map((d) => ({
    id: d.id,
    day: d.day,
    position: d.position,
    slots: (d.slots ?? []).map((s) => ({
      id: s.id,
      poll_day_id: s.poll_day_id,
      slot_time: s.slot_time,
      vote_count: s.votes?.length ?? 0,
    })),
  }));

  const pollVotes = collectPollSlotVotes(poll);
  const slotCounts = new Map<string, number>();
  for (const day of days) {
    for (const slot of day.slots ?? []) {
      slotCounts.set(slot.id, slot.vote_count ?? 0);
    }
  }

  return {
    days,
    aggregates: buildSlotAggregates(days, slotCounts),
    totalVoters: poll.total_voters ?? countDistinctVoters(pollVotes),
    /** Distinct players who voted on each day (max 1 per player per day). */
    dayVoterCounts: dayVoterCountsForDays(days, pollVotes),
  };
}

export function isPollsV2Enabled(): boolean {
  const flag = import.meta.env.VITE_POLLS_V2;
  if (flag === "false") return false;
  return true;
}
